import type { ReactElement } from 'react';
import type { QueueItemDto } from '@gr/core/distribution/scheduling/dto';
import { Link, useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button, IconButton } from '../../../../ui-kit/Button/Button';
import { Card } from '../../../../ui-kit/Card/Card';
import { PublicationLine } from '../PublicationLine/PublicationLine';
import styles from './QueueList.module.scss';

export interface QueueListProps {
  blogId: string;
  timezone: string;
  items: readonly QueueItemDto[];
  editable: boolean;
  pending: boolean;
  onMove(index: number, delta: number): void;
  onRemove(item: QueueItemDto): void;
}

/** SCH-UC-3, SCH-UC-4, SCH-RULE-2: очередь постов с расчётным временем каждой публикации. */
export function QueueList({ blogId, timezone, items, editable, pending, onMove, onRemove }: QueueListProps): ReactElement {
  const { t } = useT();
  return (
    <ol className={styles.list} data-testid="queue">
      {items.map((item, index) => (
        <li key={item.id}>
          <Card
            title={
              <span className="u-row">
                <Badge>{t('scheduling.queue.position', { n: index + 1 })}</Badge>
                <Link to="post" params={{ blogId, postId: item.postId }}>
                  {item.postTitle}
                </Link>
              </span>
            }
            actions={
              editable ? (
                <>
                  <IconButton icon="up" label={t('scheduling.queue.up')} disabled={index === 0 || pending} onClick={() => onMove(index, -1)} />
                  <IconButton icon="down" label={t('scheduling.queue.down')} disabled={index === items.length - 1 || pending} onClick={() => onMove(index, 1)} />
                  <Button size="sm" variant="ghost" icon="close" disabled={pending} onClick={() => onRemove(item)}>
                    {t('scheduling.queue.remove')}
                  </Button>
                </>
              ) : null
            }
          >
            <div className="u-stack">
              <span className="u-muted u-small">{item.schemeName}</span>
              {item.unplanned ? <Alert tone="warning">{t('scheduling.queue.unplanned')}</Alert> : null}
              {item.publications.map((publication) => (
                <PublicationLine key={publication.id} publication={publication} timezone={timezone} />
              ))}
            </div>
          </Card>
        </li>
      ))}
    </ol>
  );
}
